// src/utils/notification/notificationHelpers.js

/**
 * Format a date as a relative timestamp (e.g., "2 hours ago")
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted relative timestamp
 */
export const formatRelativeTime = (date) => {
  if (!date) return "";

  const now = new Date();
  const targetDate = new Date(date);
  const diffInSeconds = Math.floor((now - targetDate) / 1000);

  // Less than a minute
  if (diffInSeconds < 60) {
    return "Just now";
  }

  // Less than an hour
  if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
  }

  // Less than a day
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  }

  // Less than a week
  if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} ${days === 1 ? "day" : "days"} ago`;
  }

  // Less than a month (30 days)
  if (diffInSeconds < 2592000) {
    const weeks = Math.floor(diffInSeconds / 604800);
    return `${weeks} ${weeks === 1 ? "week" : "weeks"} ago`;
  }

  // More than a month - show absolute date
  return formatAbsoluteDate(targetDate);
};

/**
 * Format a date as an absolute date string
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted absolute date
 */
export const formatAbsoluteDate = (date) => {
  if (!date) return "";

  const targetDate = new Date(date);
  const now = new Date();

  // Same year - show month and day
  if (targetDate.getFullYear() === now.getFullYear()) {
    return targetDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  }

  // Different year - show month, day, and year
  return targetDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/**
 * Format a date as a full timestamp for tooltips
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted full timestamp
 */
export const formatFullTimestamp = (date) => {
  if (!date) return "";

  const targetDate = new Date(date);
  return targetDate.toLocaleString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
};

/**
 * Check if a timestamp should show relative or absolute time
 * @param {string|Date} date - The date to check
 * @returns {boolean} True if should show relative time, false for absolute
 */
export const shouldShowRelativeTime = (date) => {
  if (!date) return false;

  const now = new Date();
  const targetDate = new Date(date);
  const diffInHours = Math.floor((now - targetDate) / (1000 * 60 * 60));

  // Show relative time for anything within 24 hours
  return diffInHours < 24;
};

/**
 * Format notification message based on type and actor data
 * @param {Object} notification - The notification object
 * @returns {string} Formatted notification message
 */
export const formatNotificationMessage = (notification) => {
  if (!notification || !notification.type) return "";

  const actorName =
    notification.metadata?.actorName ||
    notification.actor?.name ||
    notification.metadata?.actorUsername ||
    notification.actor?.username ||
    "Someone";

  switch (notification.type) {
    case "follow":
      return `${actorName} started following you`;
    case "unfollow":
      return `${actorName} unfollowed you`;
    case "fighter_cancelled":
      const { fighterName, eventName } = notification.metadata || {};
      return `Fight Cancelled: ${fighterName}'s fight has been cancelled for ${eventName}. They will score 0 points.`;
    default:
      return notification.message || "New notification";
  }
};

/**
 * Get notification icon based on type
 * @param {string} type - The notification type
 * @returns {string} Icon name or emoji
 */
export const getNotificationIcon = (type) => {
  switch (type) {
    case "follow":
      return "👤";
    case "unfollow":
      return "👤";
    case "fighter_cancelled":
      return "⚠️";
    default:
      return "🔔";
  }
};

/**
 * Get notification color based on type and read status
 * @param {Object} notification - The notification object
 * @returns {string} CSS color class or hex color
 */
export const getNotificationColor = (notification) => {
  if (!notification.read) {
    switch (notification.type) {
      case "follow":
        return "text-blue-600";
      case "unfollow":
        return "text-gray-600";
      case "fighter_cancelled":
        return "text-orange-600";
      default:
        return "text-blue-600";
    }
  }

  return "text-gray-500";
};

/**
 * Check if notification is recent (within last hour)
 * @param {string|Date} date - The notification date
 * @returns {boolean} True if notification is recent
 */
export const isRecentNotification = (date) => {
  if (!date) return false;

  const now = new Date();
  const targetDate = new Date(date);
  const diffInMinutes = Math.floor((now - targetDate) / (1000 * 60));

  return diffInMinutes < 60;
};

/**
 * Group notifications by date
 * @param {Array} notifications - Array of notification objects
 * @returns {Object} Notifications grouped by date
 */
export const groupNotificationsByDate = (notifications) => {
  if (!Array.isArray(notifications)) return {};

  const groups = {};
  const now = new Date();

  notifications.forEach((notification) => {
    const date = new Date(notification.createdAt);
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    let groupKey;
    if (diffInDays === 0) {
      groupKey = "Today";
    } else if (diffInDays === 1) {
      groupKey = "Yesterday";
    } else if (diffInDays < 7) {
      groupKey = `${diffInDays} days ago`;
    } else {
      groupKey = formatAbsoluteDate(date);
    }

    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(notification);
  });

  return groups;
};

/**
 * Sort notifications by date (newest first)
 * @param {Array} notifications - Array of notification objects
 * @returns {Array} Sorted notifications
 */
export const sortNotificationsByDate = (notifications) => {
  if (!Array.isArray(notifications)) return [];

  return [...notifications].sort((a, b) => {
    const dateA = new Date(a.createdAt);
    const dateB = new Date(b.createdAt);
    return dateB - dateA;
  });
};

/**
 * Filter notifications by type
 * @param {Array} notifications - Array of notification objects
 * @param {string} type - Type to filter by
 * @returns {Array} Filtered notifications
 */
export const filterNotificationsByType = (notifications, type) => {
  if (!Array.isArray(notifications)) return [];
  if (!type) return notifications;

  return notifications.filter((notification) => notification.type === type);
};

/**
 * Filter notifications by read status
 * @param {Array} notifications - Array of notification objects
 * @param {boolean} read - Read status to filter by
 * @returns {Array} Filtered notifications
 */
export const filterNotificationsByReadStatus = (notifications, read) => {
  if (!Array.isArray(notifications)) return [];
  if (typeof read !== "boolean") return notifications;

  return notifications.filter((notification) => notification.read === read);
};

/**
 * Get unread notification count
 * @param {Array} notifications - Array of notification objects
 * @returns {number} Count of unread notifications
 */
export const getUnreadNotificationCount = (notifications) => {
  if (!Array.isArray(notifications)) return 0;

  return notifications.filter((notification) => !notification.read).length;
};

/**
 * Validate notification object structure
 * @param {Object} notification - Notification object to validate
 * @returns {boolean} True if valid notification structure
 */
export const isValidNotification = (notification) => {
  if (!notification || typeof notification !== "object") return false;

  const requiredFields = ["_id", "type", "createdAt"];
  return requiredFields.every((field) => notification.hasOwnProperty(field));
};

/**
 * Sanitize notification data for display
 * @param {Object} notification - Notification object to sanitize
 * @returns {Object} Sanitized notification object
 */
export const sanitizeNotification = (notification) => {
  if (!isValidNotification(notification)) return null;

  return {
    _id: notification._id,
    type: notification.type,
    actor: notification.actor || {},
    metadata: notification.metadata || {},
    read: Boolean(notification.read),
    createdAt: notification.createdAt,
    message: formatNotificationMessage(notification),
  };
};

/**
 * Create notification query parameters for API calls
 * @param {Object} filters - Filter options
 * @param {Object} pagination - Pagination options
 * @returns {Object} Query parameters object
 */
export const createNotificationQueryParams = (
  filters = {},
  pagination = {},
) => {
  const params = {
    page: pagination.page || 1,
    limit: pagination.limit || 20,
    sort: filters.sort || "createdAt:desc",
  };

  if (filters.type) {
    params.type = filters.type;
  }

  if (typeof filters.read === "boolean") {
    params.read = filters.read;
  }

  return params;
};
